import React, { useMemo, useState, useCallback } from "react";
import { Box, Button, Typography, Grid, Tooltip, Chip, Paper, IconButton, Collapse } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../store";
import { SetWorldAction } from "../../store/user/actions";
import { useNavigate } from "react-router-dom";
import { DetailedWorldCard } from "./DetailedWorldCard";
import ForestIcon from "@mui/icons-material/Forest";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PublicIcon from "@mui/icons-material/Public";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockIcon from "@mui/icons-material/Lock";
import "../Tools.css";

interface WorldHeaderProps {
  onWorldSelect?: (worldId: string) => void;
  isCompact?: boolean;
}

export const WorldHeader: React.FC<WorldHeaderProps> = ({
  onWorldSelect,
  isCompact = false,
}) => {
  const { loginInfo, gameStatus } = useSelector(
    (state: AppState) => state.userModule
  );
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const currentWorld = gameStatus.world; // Get current world from state
  const [expanded, setExpanded] = useState<boolean>(!isCompact);

  const handleWorldSelect = useCallback(
    (worldId: string) => {
      dispatch(SetWorldAction(Number(worldId)));
      if (onWorldSelect) {
        onWorldSelect(worldId);
      }
      if (isCompact) {
        setExpanded(false); // Collapse after selection if in compact mode
      }
    },
    [dispatch, onWorldSelect, isCompact]
  );

  // Conditionally render worlds based on isCompact prop
  const worldsToDisplay = useMemo(() => {
    if (isCompact && currentWorld) {
      return [currentWorld]; // Display only the current world in compact mode
    } else {
      return [1, 2, 3]; // Display all worlds otherwise
    }
  }, [isCompact, currentWorld]);

  const getWorldInfo = (worldId: number) => {
    return gameStatus.reward_info?.find((item: any) => item.id === worldId);
  };

  const currentWorldInfo = currentWorld ? getWorldInfo(currentWorld) : null;

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Box>
      {/* Compact Header - Always visible */}
      <Paper 
        sx={{ 
          p: 1.5, 
          mb: expanded ? 2 : 0, 
          background: 'linear-gradient(135deg, #4a300b 0%, #362208 100%)',
          borderRadius: '12px',
          border: '2px solid #7d5014',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
        }}
        onClick={toggleExpanded}
      >
        {/* Wood grain overlay */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4, 
          zIndex: 0 
        }} />

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentWorld ? (
              <>
                <PublicIcon sx={{ color: "#f5e1a3", mr: 1, fontSize: "1.8rem" }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="#f5e1a3">
                    World {currentWorld}
                  </Typography>
                  {currentWorldInfo && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`${currentWorldInfo.min_reward} - ${currentWorldInfo.max_reward} ANTLERS`} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(233, 76, 88, 0.2)', 
                          color: '#e94c58',
                          border: '1px solid rgba(233, 76, 88, 0.3)',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }} 
                      />
                      <Chip 
                        label={`${currentWorldInfo.delay / 3600}h Exploration`} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(46, 202, 106, 0.2)', 
                          color: '#2eca6a',
                          border: '1px solid rgba(46, 202, 106, 0.3)',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }} 
                      />
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <>
                <ForestIcon sx={{ color: "#ffcc33", mr: 1, fontSize: "1.8rem" }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="#ffcc33">
                    No World Selected
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Click to select a world
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          
          <IconButton 
            size="small" 
            sx={{ color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Paper>

      {/* Expanded World Selection - Only shown when expanded */}
      <Collapse in={expanded}>
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'rgba(0,0,0,0.2)', 
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Typography variant="body1" color="white" fontWeight="bold" mb={2} textAlign="center">
            Select Your World
          </Typography>
          
          <Grid container spacing={2} justifyContent="center">
            {gameStatus.reward_info
              ?.filter((item: any) => 
                loginInfo.open_worlds?.includes(item.id)
              )
              .map((item: any) => {
                return (
                  <Grid item key={item.id} xs={12} sm={6} md={4}>
                    <DetailedWorldCard
                      index={item.id}
                      isOpen={loginInfo.open_worlds?.includes(item.id)}
                      cost={parseFloat(item.unlock_fee?.split(" ")?.[0])}
                      rewardInfo={{
                        fees: {
                          og: parseFloat(item.base_fee?.split(" ")?.[0]),
                          abyssal: parseFloat(item.base_fee?.split(" ")?.[0]),
                          fawn: parseFloat(item.base_fee?.split(" ")?.[0]) * 5,
                          promo: parseFloat(item.base_fee?.split(" ")?.[0]) * 0.1,
                        },
                        cost_multi: {
                          og: 1,
                          abyssal: 1,
                          fawn: 1,
                          promo: 0.1,
                        },
                        reward_multi: {
                          og: 1,
                          abyssal: 1,
                          fawn: 3,
                          promo: 0.1,
                        },
                        nft_drop_rarity: item.nft_chance == 0 ? "None" : "Enabled",
                        exploration_durations: [item.delay / 3600],
                        minReward: parseFloat(item.min_reward).toFixed(4),
                        maxReward: parseFloat(item.max_reward).toFixed(4),
                        baseFee: parseFloat(item.base_fee).toFixed(4),
                        ...item,
                      }}
                      onCardClick={() => {
                        handleWorldSelect(item.id.toString());
                      }}
                      isMobile={false}
                      isDetailedView={true}
                    />
                  </Grid>
                );
              })}
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/worlds")}
              sx={{
                color: "#f5e1a3",
                borderColor: "#f5e1a3",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(245, 225, 163, 0.1)",
                },
              }}
            >
              Go to World Selection
            </Button>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};
